import { useAuth } from "../contexts/AuthContext";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { Avatar } from "../design/avatar";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "../design/dropdown";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
} from "../design/navbar";
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from "../design/sidebar";
import { SidebarLayout } from "../design/sidebar-layout";
import {
  ArrowRightStartOnRectangleIcon,
  ChevronUpIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  FolderIcon,
  HomeIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
  UsersIcon,
  DocumentIcon,
  FaceSmileIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/20/solid";
import { getInitials } from "../utils/string";
import { SparklesIcon } from "@heroicons/react/16/solid";
import LogoHorizontal from "./LogoHorizontal";
import RoleSwitcher from "./RoleSwitcher";
import ClaimsDebugPanel from "./ClaimsDebugPanel";
import SupportChatBubble from "./SupportChatBubble";

function AccountDropdownMenu({
  anchor,
}: {
  anchor: "top start" | "bottom end";
}) {
  const { isAdmin } = useAuth();

  return (
    <DropdownMenu className="min-w-64" anchor={anchor}>
      <DropdownItem href={isAdmin ? "/settings" : "/profile"}>
        <UserCircleIcon />
        <DropdownLabel>{isAdmin ? "My account" : "My profile"}</DropdownLabel>
      </DropdownItem>
      <DropdownDivider />
      <DropdownItem href="https://www.visitproof.com/privacy-policy">
        <ShieldCheckIcon />
        <DropdownLabel>Privacy policy</DropdownLabel>
      </DropdownItem>
      <DropdownItem href="https://www.visitproof.com/terms-of-service">
        <DocumentIcon />
        <DropdownLabel>Terms of Service</DropdownLabel>
      </DropdownItem>
      <DropdownItem href="mailto:adam@visitproof.com">
        <LightBulbIcon />
        <DropdownLabel>Share feedback</DropdownLabel>
      </DropdownItem>
      <DropdownDivider />
      <DropdownItem
        onClick={async () => {
          await signOut(auth);
        }}
      >
        <ArrowRightStartOnRectangleIcon />
        <DropdownLabel>Sign out</DropdownLabel>
      </DropdownItem>
    </DropdownMenu>
  );
}

// Admin sidebar navigation
const AdminSidebar: React.FC = () => {
  return (
    <SidebarBody>
      <SidebarSection>
        <SidebarItem href="/dashboard">
          <HomeIcon />
          <SidebarLabel>Home</SidebarLabel>
        </SidebarItem>
        {/*
          Inquiries link removed: there is no /inquiries route registered in
          App.tsx (the catch-all redirects to /dashboard), so this produced a
          broken link. Restore this SidebarItem (gated on the account's
          isInquiryEnabled flag) once an Inquiries page and route exist.
        */}
        <SidebarItem href="/scheduler" id="scheduler-sidebar-item">
          <CalendarDaysIcon />
          <SidebarLabel>Scheduler</SidebarLabel>
        </SidebarItem>
        <SidebarItem href="/cases" id="cases-sidebar-item">
          <FolderIcon />
          <SidebarLabel>Cases</SidebarLabel>
        </SidebarItem>
        <SidebarItem href="/visits" id="visits-sidebar-item">
          <ClipboardDocumentListIcon />
          <SidebarLabel>Visits</SidebarLabel>
        </SidebarItem>
        <SidebarItem href="/messages" id="messages-sidebar-item">
          <ChatBubbleLeftRightIcon />
          <SidebarLabel>Messages</SidebarLabel>
        </SidebarItem>
        <SidebarItem href="/supervisors" id="supervisors-sidebar-item">
          <ShieldCheckIcon />
          <SidebarLabel>Monitors</SidebarLabel>
        </SidebarItem>
        <SidebarItem href="/parents" id="parents-sidebar-item">
          <UserGroupIcon />
          <SidebarLabel>Parents</SidebarLabel>
        </SidebarItem>
        <SidebarItem href="/children" id="children-sidebar-item">
          <FaceSmileIcon />
          <SidebarLabel>Children</SidebarLabel>
        </SidebarItem>
        <SidebarItem href="/reports" id="reports-sidebar-item">
          <DocumentTextIcon />
          <SidebarLabel>Reports</SidebarLabel>
        </SidebarItem>
        <SidebarItem href="/demographics" id="demographics-sidebar-item">
          <ChartBarIcon />
          <SidebarLabel>Demographics</SidebarLabel>
        </SidebarItem>
        <SidebarItem href="/team" id="team-sidebar-item">
          <UsersIcon />
          <SidebarLabel>Team</SidebarLabel>
        </SidebarItem>
      </SidebarSection>

      <SidebarSpacer />

      <SidebarSection>
        <SidebarItem href="mailto:adam@visitproof.com">
          <QuestionMarkCircleIcon />
          <SidebarLabel>Support</SidebarLabel>
        </SidebarItem>
        <SidebarItem id="changelog-sidebar-item">
          <SparklesIcon />
          <SidebarLabel>Change Log</SidebarLabel>
        </SidebarItem>
        <SidebarItem
          href="https://produktly.com/roadmap/produktly/ebc22c6f-58c1-4326-94d7-d0c77c1da747?t=roadmap"
          target="_blank"
          rel="noreferrer"
        >
          <ClipboardDocumentCheckIcon />
          <SidebarLabel>Roadmap</SidebarLabel>
        </SidebarItem>
      </SidebarSection>
    </SidebarBody>
  );
};

// Supervisor/Monitor sidebar navigation
const SupervisorSidebar: React.FC = () => {
  return (
    <SidebarBody>
      <SidebarSection>
        <SidebarItem href="/dashboard">
          <HomeIcon />
          <SidebarLabel>Dashboard</SidebarLabel>
        </SidebarItem>
        <SidebarItem href="/visits">
          <ClipboardDocumentListIcon />
          <SidebarLabel>My Visits</SidebarLabel>
        </SidebarItem>
        <SidebarItem href="/reports">
          <DocumentTextIcon />
          <SidebarLabel>My Reports</SidebarLabel>
        </SidebarItem>
      </SidebarSection>

      <SidebarSpacer />

      <SidebarSection>
        <SidebarItem href="mailto:adam@visitproof.com">
          <QuestionMarkCircleIcon />
          <SidebarLabel>Support</SidebarLabel>
        </SidebarItem>
      </SidebarSection>
    </SidebarBody>
  );
};

// Parent sidebar navigation
const ParentSidebar: React.FC = () => {
  return (
    <SidebarBody>
      <SidebarSection>
        <SidebarItem href="/dashboard">
          <HomeIcon />
          <SidebarLabel>Dashboard</SidebarLabel>
        </SidebarItem>
        <SidebarItem href="/visits">
          <ClipboardDocumentListIcon />
          <SidebarLabel>My Visits</SidebarLabel>
        </SidebarItem>
      </SidebarSection>

      <SidebarSpacer />

      <SidebarSection>
        <SidebarItem href="mailto:adam@visitproof.com">
          <QuestionMarkCircleIcon />
          <SidebarLabel>Support</SidebarLabel>
        </SidebarItem>
      </SidebarSection>
    </SidebarBody>
  );
};

const RoleBasedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, isAdmin, isMonitor, isParent } = useAuth();

  // Show loading indicator while claims are being fetched
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-zinc-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 rounded-full border-zinc-200 border-t-zinc-600 animate-spin" />
          <span className="text-sm text-zinc-500 dark:text-zinc-400">Loading...</span>
        </div>
      </div>
    );
  }

  // Determine which sidebar to show based on role
  const renderSidebar = () => {
    if (isAdmin) {
      return <AdminSidebar />;
    }
    if (isMonitor) {
      return <SupervisorSidebar />;
    }
    if (isParent) {
      return <ParentSidebar />;
    }
    // Fallback - shouldn't happen
    return <SupervisorSidebar />;
  };

  return (
    <SidebarLayout
      navbar={
        <Navbar>
          <NavbarSpacer />
          <NavbarSection>
            <Dropdown>
              <DropdownButton as={NavbarItem}>
                <Avatar square />
              </DropdownButton>
              <AccountDropdownMenu anchor="bottom end" />
            </Dropdown>
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <Sidebar>
          <div>
            <LogoHorizontal className="h-8 mt-6 mb-2 ml-6" href="https://www.visitproof.com" />
          </div>
          {renderSidebar()}
          <SidebarFooter>
            <RoleSwitcher />
            <Dropdown>
              <DropdownButton as={SidebarItem}>
                <span className="flex items-center min-w-0 gap-3">
                  <Avatar
                    src={user?.photoURL}
                    className="size-10"
                    square
                    alt=""
                    initials={getInitials(user?.displayName ?? undefined)}
                  />
                  <span className="min-w-0">
                    <span className="block font-medium truncate text-sm/5 text-zinc-950 dark:text-white">
                      {user?.displayName}
                    </span>
                    <span className="block font-normal truncate text-xs/5 text-zinc-500 dark:text-zinc-400">
                      {user?.email || user?.phoneNumber}
                    </span>
                  </span>
                </span>
                <ChevronUpIcon />
              </DropdownButton>
              <AccountDropdownMenu anchor="top start" />
            </Dropdown>
          </SidebarFooter>
        </Sidebar>
      }
    >
      {children}
      <ClaimsDebugPanel />
      <SupportChatBubble />
    </SidebarLayout>
  );
};

export default RoleBasedLayout;
